import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Human resources",
  title: "HR verification agent",
  cardSummary:
    "Verify that a candidate's certificates, references and stated history line up — before the offer goes out.",
  heroSubhead:
    "We design, build and deploy an AI agent that runs a candidate's certificates, references and stated employment history through parallel checks at once, flags every mismatch with evidence, and hands your HR team a hire-ready verification summary — while the hiring decision stays with a human.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "A bad hire in the region costs months of salary and rehiring — this catches the doctored cert or padded tenure before the offer, and clears clean candidates in minutes instead of days.",
    complexityLabel: "Medium",
    complexityWhy:
      "Vision/OCR on cert scans and reference emails, several parallel checks reconciled, and PDPA/PDPC-grade handling of IC and candidate data — but no money movement and no regulator on the critical path.",
    timeToLive: "~a few weeks (one hiring pipeline)",
    bestFit: "HR teams & recruitment agencies vetting candidates before an offer",
  },
  outcomes: [
    "Catch the doctored cert or padded tenure before the offer — not after a costly bad hire",
    "Clean candidates cleared in minutes, so good hires don't slip to a faster competitor",
    "The same checks on every candidate — no favourite-CV shortcuts, an audit trail if challenged",
    "HR recruiters back to interviewing and closing, not chasing references by phone",
  ],
  impact: {
    manualLabel: "Manual vetting today",
    manualValue: "~2–4 hours (spread over days)",
    agentLabel: "With the agent",
    agentValue: "~10–15 min review",
    saved:
      "≈ 2–3 hours of recruiter time saved per candidate, and days of calendar time collapsed to same-day. At 80 hires a month that's roughly 160–240 hours — about one full-time recruiter freed to close. And one prevented bad hire (often 3–6 months of salary) pays for the whole build many times over.",
    note: "Indicative; actual time depends on the number of certificates and referees and how quickly referees respond.",
  },
  businessProblem:
    "An employer or recruitment agency in the region vets each candidate as a stack: a CV, scanned SPM/A-levels, diploma or degree certificates, a few referee contacts, and a stated employment history. Most of a recruiter's day goes to the mechanical, waiting-heavy part — reading each certificate, chasing referees by phone and email, and eyeballing whether the stated dates and titles actually line up with the CV — not to judging fit. It is slow and it stalls: references sit unanswered for days while a good candidate takes another offer. And because it is manual, the diligence quietly varies — a rushed req gets a lighter check than a slow week, and a doctored certificate or a padded tenure slips through.",
  manualWorkflow: {
    steps: [
      "Open the candidate pack — CV, certificate scans, referee list",
      "Read each certificate by eye and note the institution, qualification and dates",
      "Email and phone each referee, then wait days for a reply",
      "Cross-check stated titles, dates and salary against the CV and references",
      "Chase the gaps — a missing cert, an overlapping job, an unreachable referee",
      "Only then form a view and move to an offer",
    ],
    breaks:
      "The bottleneck is verification — reading documents and waiting on references — done serially, one item at a time. It's the slow, repetitive part that stalls for days and quietly varies in thoroughness between recruiters.",
  },
  agenticWorkflow: {
    narrative:
      "We redesign vetting as a parallelization (sectioning) workflow: instead of checking one thing at a time, the agent fans the candidate pack out into independent checks — certificates, references, and stated history — that run at once, then reconciles the results into one evidenced summary. Independent checks in parallel is exactly what collapses days into minutes.",
    steps: [
      "Split the pack into independent checks",
      "Verify certificates (read + validate)",
      "Send & read reference responses",
      "Reconcile stated history for gaps & overlaps",
      "Merge into one evidenced summary",
    ],
    inLabel: "A candidate pack arrives",
    outLabel: "A hire-ready verification summary",
    loopAround: {
      index: 0,
      label: "The certificate, reference and history checks run in parallel, not one after another — the parallelization (sectioning) pattern.",
    },
    humanStep: "The hiring manager makes the hire decision",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "In the candidate pack",
    inMessage:
      "CV: \"Senior Analyst, Maju Corp, Jan 2020 – Present\". Referee (Maju Corp HR) reply: \"employed as Analyst, Mar 2021 to Aug 2023\". Degree cert: BSc, Universiti XYZ, conferred 2019.",
    outLabel: "What the agent flags",
    outLines: [
      "Degree certificate reads and validates — institution, qualification and year present",
      "Referee reply received and matched to the stated employer",
      "Stated title \"Senior Analyst\" cross-checked against the referee's \"Analyst\"",
      "Stated dates cross-checked against the referee's confirmed dates",
    ],
    outResult:
      "2 discrepancies flagged: title (Senior Analyst stated vs Analyst confirmed) · tenure (Jan 2020–Present stated vs Mar 2021–Aug 2023 confirmed)",
    note: "Every flag cites the exact document or referee reply · your recruiter makes the hire call · ~RM 0.50–1.00 per candidate.",
  },
  workflowDetail: [
    {
      title: "Split the pack into independent checks",
      detail:
        "The agent takes the candidate pack — CV, certificate scans, referee contacts, stated history — and fans it out into independent verification tracks that don't depend on each other, so they can run at the same time rather than in a queue.",
    },
    {
      title: "Verify the certificates",
      detail:
        "It reads each certificate — including phone photos and scans — using vision to extract the institution, qualification, and dates, checks them for internal consistency and against the CV's claims, and flags anything that looks altered or doesn't match.",
    },
    {
      title: "Send and read reference responses",
      detail:
        "It sends structured reference requests to each referee and reads the replies as they arrive, extracting the confirmed title, dates and (where given) reason for leaving — so a recruiter isn't manually chasing and transcribing phone calls.",
    },
    {
      title: "Reconcile the stated history",
      detail:
        "It builds a timeline from the CV and checks it for gaps, overlapping employment and dates that contradict the certificates or the reference replies — the padded-tenure and unexplained-gap checks a rushed manual review misses.",
    },
    {
      title: "Merge into one summary",
      detail:
        "It merges the parallel results into a single one-page summary: which checks passed, and the two or three items to look at — each with the certificate, referee reply or CV line it came from.",
    },
    {
      title: "Hand to a human",
      detail:
        "The hiring manager reviews the flagged items and makes the hire decision. Nothing is auto-rejected — the agent surfaces evidence, a human decides.",
    },
  ],
  architecture: [
    "Pattern: a parallelization (sectioning) workflow on the Claude Agent SDK — the certificate, reference and history checks are dispatched as concurrent sub-tasks and fanned back in, each tool exposed via an in-process MCP server.",
    "Context: each parallel branch gets only the slice it needs (a cert branch sees the cert + the CV claim it's testing, not the whole pack) — a context-engineering choice that keeps each check focused, cheaper, and injection-isolated.",
    "Tools: separated read vs write — read-only cert/OCR extractors, a CV/history parser and a timeline reconciler on one side; a single gated, idempotent reference-request sender on the other (a retry can't email a referee twice).",
    "Guardrails & least-privilege: schema-locked JSON output, no write access to the ATS/HR system of record, it never auto-rejects a candidate, and low-confidence certs or unmatched references route to the recruiter's queue.",
    "Untrusted input & PDPA/PDPC: certificate text and referee replies are treated as untrusted (prompt-injection resistant — a doctored PDF or a hostile reference email can't steer the outcome); IC numbers, salary and candidate PII are handled PDPA/PDPC-aligned with data-residency in scope.",
    "Recovery & escalation: a non-responding referee, an unreadable scan or a conflicting result is retried then escalated to a human — every branch ends at a person, never a silent pass or a silent reject.",
    "Observability & audit: every parallel check, tool call and the merged result is logged and traceable, so any verification summary — and the recruiter's final call against it — can be reconstructed if a rejected candidate challenges it.",
    "Eval harness: a golden set of historical candidate packs — including known doctored certs, padded tenures and unexplained gaps — with deterministic checks plus LLM-as-judge on the fuzzy calls, regression-tested on every change before it touches live candidates.",
  ],
  models: [
    {
      task: "Read & validate certificates (incl. scans & photos)",
      model: "Claude Sonnet",
      why: "Reliable vision and structured extraction on real-world certificate scans and phone photos.",
    },
    {
      task: "Reconcile history & reason across the checks",
      model: "Claude Sonnet",
      why: "The workhorse — strong reasoning to line dates, titles and references up and catch the subtle mismatches.",
    },
    {
      task: "Classify & extract reference replies",
      model: "Claude Haiku",
      why: "Cheapest and fastest for pulling confirmed title/dates out of a straightforward referee email.",
    },
    {
      task: "Adjudicate borderline / contested cases",
      model: "Claude Opus",
      why: "Invoked only when a check is genuinely ambiguous — a small fraction of candidates.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per candidate",
        value: "~RM 0.50–1.00",
        note: "certificate reads + reference parsing + reconciliation + summary, at Claude Sonnet/Haiku token rates",
      },
      {
        label: "AI usage — at 100 candidates/month",
        value: "~RM 45–85 / month",
        note: "roughly half with the Batch API for non-urgent overnight verification runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "a focused single-pipeline agent is a few weeks of engineering; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the recruiter time it frees and one prevented bad hire",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on the number of certificates and referees and case complexity. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  howWeBuild:
    "It's wired straight into your ATS or recruitment inbox through connected tools, so certificate scans and referee replies flow in without manual exports. Because the checks run in parallel and each writes its evidence as it goes, the summary your recruiter reads is fully sourced — every flag traces back to the exact certificate, referee reply or CV line it came from — and the agent never rejects a candidate on its own.",
  agentLoop: [
    "the candidate pack — certs, referees & stated history",
    "run the checks in parallel & request references (MCP tools)",
    "reconcile the parallel results & re-check its own flags",
  ],
  integrations: [
    "Your ATS / recruitment system",
    "Recruitment inbox or document store (certificate scans)",
    "Email / reference-request channel for referees",
    "Optional: an education or credential verification lookup",
  ],
  modelChoice: {
    approach: "Single model, parallel branches",
    rationale:
      "The checks are independent but each is a bounded, well-defined task sharing the same candidate context, so one well-instrumented model running parallel branches is the right call — reliable, cheaper, and easy to audit — rather than a full multi-agent design.",
    costNote:
      "Fanning out into parallel branches is not the same as a ≈15×-token multi-agent system; we reserve that for genuinely open-ended, breadth-first research, not a fixed set of verification checks.",
  },
  evaluation: [
    "Deterministic checks first — certificate fields present, dates line up, referee replied",
    "Self-verification — the agent re-checks each flag against its source before surfacing it",
    "Business KPIs — recruiter time per candidate, time-to-clear, consistency, and doctored/padded cases caught",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure the current manual vetting on a sample — recruiter time per candidate, how long a full verification takes end to end, how consistently two recruiters reach the same conclusion, and how many issues slip through. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reach the right verification outcome?",
        metrics: [
          {
            name: "Agreement with a senior recruiter",
            measures: "Its summary, flags and recommended checks match an expert's on held-out candidate packs",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the approach sound and complete?",
        metrics: [
          {
            name: "Required checks executed",
            measures: "Every mandated check — each certificate, each referee, the full timeline — is run, nothing skipped",
            target: "100%",
          },
          {
            name: "Effective parallelism",
            measures: "Independent checks actually run concurrently, collapsing calendar time, without dropping any",
            target: "monitored",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Certificate field extraction accuracy",
            measures: "Did it read each certificate — institution, qualification, dates — correctly?",
            target: "≥ 98%",
          },
          {
            name: "Discrepancy recall (catch rate)",
            measures: "Of the real mismatches in the golden set — doctored certs, padded tenure, gaps — how many it flags",
            target: "≥ 95%",
          },
          {
            name: "False-flag rate",
            measures: "How often it flags something that isn't actually a problem",
            target: "≤ 10%",
          },
          {
            name: "No hallucinated findings",
            measures: "Every flag in the summary traces to a certificate, referee reply or CV line",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not touch a live candidate until it beats the human baseline and clears these GPA targets on a golden set of historical packs — including known doctored certificates and padded histories. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical candidate packs — including the ones with doctored certs and padded tenures you already caught — and prove reliability before it touches live candidates. A focused single-pipeline rollout is a matter of weeks.",
  ctaTitle: "Is your HR team verifying candidates by hand?",
  ctaDescription:
    "Tell us your hiring volume and what you check today, and we'll tell you honestly whether an HR verification agent is worth building — and what it would take.",
  faqs: [
    {
      question: "Does the agent reject candidates?",
      answer:
        "No. It reads, verifies and flags; a human hiring manager makes the hire or no-hire decision. Human-in-the-loop is essential — the agent surfaces evidence, it never auto-rejects a candidate.",
    },
    {
      question: "How is candidate data kept compliant with PDPA/PDPC?",
      answer:
        "With a full audit trail so any verification can be reconstructed, and built around PDPA/PDPC requirements for where candidate data — IC numbers, certificates, salary — is processed, stored and accessed. Treat this as a starting point for your compliance team, not legal advice.",
    },
    {
      question: "Can it actually catch a doctored certificate?",
      answer:
        "It catches internal inconsistencies and mismatches against the CV and references, and flags certs that look altered. For authoritative confirmation we can wire in an education or credential verification lookup — but the final judgement always sits with your recruiter.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept for one focused hiring pipeline is usually a matter of weeks; a full rollout takes longer, mostly because of integration and getting recruiters comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Sumber manusia",
  title: "Ejen pengesahan HR",
  cardSummary:
    "Sahkan bahawa sijil, rujukan dan sejarah kerja calon benar-benar sepadan — sebelum tawaran dihantar.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang menjalankan sijil, rujukan dan sejarah pekerjaan calon melalui semakan selari secara serentak, menandakan setiap percanggahan dengan bukti, dan menyediakan ringkasan pengesahan sedia-ambil untuk pasukan HR anda — sementara keputusan pengambilan kekal dengan manusia.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Pengambilan yang salah di rantau ini menelan beberapa bulan gaji dan kos pengambilan semula — ini menangkap sijil yang diusik atau tempoh kerja yang dibesar-besarkan sebelum tawaran, dan meluluskan calon bersih dalam minit bukan hari.",
    complexityLabel: "Sederhana",
    complexityWhy:
      "Visi/OCR pada imbasan sijil dan e-mel rujukan, beberapa semakan selari diselaraskan, dan pengendalian bertaraf PDPA/PDPC untuk IC dan data calon — tetapi tiada pergerakan wang dan tiada pengawal selia pada laluan kritikal.",
    timeToLive: "~beberapa minggu (satu saluran pengambilan)",
    bestFit: "Pasukan HR & agensi pengambilan yang menapis calon sebelum tawaran",
  },
  outcomes: [
    "Tangkap sijil diusik atau tempoh kerja dibesar-besarkan sebelum tawaran — bukan selepas pengambilan salah yang mahal",
    "Calon bersih diluluskan dalam minit, supaya calon baik tidak terlepas kepada pesaing yang lebih pantas",
    "Semakan yang sama pada setiap calon — tiada jalan pintas CV kegemaran, jejak audit jika dicabar",
    "Perekrut HR kembali menemu duga dan menutup, bukan mengejar rujukan melalui telefon",
  ],
  impact: {
    manualLabel: "Penapisan manual hari ini",
    manualValue: "~2–4 jam (tersebar beberapa hari)",
    agentLabel: "Dengan ejen",
    agentValue: "~10–15 min semak",
    saved:
      "≈ 2–3 jam masa perekrut dijimatkan setiap calon, dan hari masa kalendar dikecilkan kepada hari yang sama. Pada 80 pengambilan sebulan itu kira-kira 160–240 jam — lebih kurang seorang perekrut sepenuh masa dibebaskan untuk menutup. Dan satu pengambilan salah yang dicegah (selalunya 3–6 bulan gaji) membayar keseluruhan pembinaan berkali-kali ganda.",
    note: "Anggaran; masa sebenar bergantung pada bilangan sijil dan perujuk serta kepantasan perujuk membalas.",
  },
  businessProblem:
    "Majikan atau agensi pengambilan di rantau ini menapis setiap calon sebagai satu himpunan: CV, imbasan sijil SPM/A-levels, diploma atau ijazah, beberapa kenalan perujuk, dan sejarah pekerjaan yang dinyatakan. Sebahagian besar hari perekrut dihabiskan untuk bahagian mekanikal yang penuh menunggu — membaca setiap sijil, mengejar perujuk melalui telefon dan e-mel, dan meninjau sama ada tarikh dan jawatan yang dinyatakan benar-benar sepadan dengan CV — bukan menilai kesesuaian. Ia perlahan dan tersekat: rujukan terbiar tanpa jawapan selama beberapa hari sementara calon baik menerima tawaran lain. Dan kerana ia manual, usaha wajar diam-diam berbeza — permohonan tergesa-gesa mendapat semakan lebih ringan berbanding minggu senggang, dan sijil yang diusik atau tempoh kerja yang dibesar-besarkan terlepas.",
  manualWorkflow: {
    steps: [
      "Buka himpunan calon — CV, imbasan sijil, senarai perujuk",
      "Baca setiap sijil secara manual dan catat institusi, kelayakan dan tarikh",
      "E-mel dan telefon setiap perujuk, kemudian tunggu jawapan beberapa hari",
      "Semak silang jawatan, tarikh dan gaji yang dinyatakan dengan CV dan rujukan",
      "Kejar jurang — sijil yang hilang, kerja bertindih, perujuk tidak dapat dihubungi",
      "Barulah membentuk pandangan dan bergerak ke tawaran",
    ],
    breaks:
      "Halangan ialah pengesahan — membaca dokumen dan menunggu rujukan — dilakukan secara bersiri, satu item pada satu masa. Itulah bahagian perlahan dan berulang yang tersekat selama berhari-hari dan diam-diam berbeza ketelitiannya antara perekrut.",
  },
  agenticWorkflow: {
    narrative:
      "Kami mereka bentuk semula penapisan sebagai aliran parallelization (pembahagian seksyen): daripada menyemak satu perkara pada satu masa, ejen membahagikan himpunan calon kepada semakan bebas — sijil, rujukan, dan sejarah yang dinyatakan — yang berjalan serentak, kemudian menyelaraskan hasilnya kepada satu ringkasan berbukti. Semakan bebas secara selari itulah yang mengecilkan hari kepada minit.",
    steps: [
      "Bahagikan himpunan kepada semakan bebas",
      "Sahkan sijil (baca + validasi)",
      "Hantar & baca respons rujukan",
      "Selaraskan sejarah untuk jurang & tindihan",
      "Gabungkan kepada satu ringkasan berbukti",
    ],
    inLabel: "Himpunan calon tiba",
    outLabel: "Ringkasan pengesahan sedia-ambil",
    loopAround: {
      index: 0,
      label: "Semakan sijil, rujukan dan sejarah berjalan secara selari, bukan satu demi satu — corak parallelization (pembahagian seksyen).",
    },
    humanStep: "Pengurus pengambilan membuat keputusan pengambilan",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Dalam himpunan calon",
    inMessage:
      "CV: \"Penganalisis Kanan, Maju Corp, Jan 2020 – Kini\". Perujuk (HR Maju Corp) balas: \"bekerja sebagai Penganalisis, Mac 2021 hingga Ogos 2023\". Sijil ijazah: BSc, Universiti XYZ, dianugerahkan 2019.",
    outLabel: "Apa yang ejen tandakan",
    outLines: [
      "Sijil ijazah dibaca dan disahkan — institusi, kelayakan dan tahun hadir",
      "Balasan perujuk diterima dan dipadankan dengan majikan yang dinyatakan",
      "Jawatan dinyatakan \"Penganalisis Kanan\" disemak silang dengan \"Penganalisis\" perujuk",
      "Tarikh dinyatakan disemak silang dengan tarikh disahkan perujuk",
    ],
    outResult:
      "2 percanggahan ditandakan: jawatan (Penganalisis Kanan dinyatakan vs Penganalisis disahkan) · tempoh (Jan 2020–Kini dinyatakan vs Mac 2021–Ogos 2023 disahkan)",
    note: "Setiap tanda memetik dokumen atau balasan perujuk yang tepat · perekrut anda membuat keputusan pengambilan · ~RM 0.50–1.00 setiap calon.",
  },
  workflowDetail: [
    {
      title: "Bahagikan himpunan kepada semakan bebas",
      detail:
        "Ejen mengambil himpunan calon — CV, imbasan sijil, kenalan perujuk, sejarah dinyatakan — dan membahagikannya kepada trek pengesahan bebas yang tidak bergantung antara satu sama lain, jadi ia boleh berjalan serentak dan bukan dalam baris gilir.",
    },
    {
      title: "Sahkan sijil",
      detail:
        "Ia membaca setiap sijil — termasuk foto telefon dan imbasan — menggunakan visi untuk mengekstrak institusi, kelayakan, dan tarikh, menyemak ketekalan dalamannya dan terhadap dakwaan CV, dan menandakan apa-apa yang kelihatan diubah atau tidak sepadan.",
    },
    {
      title: "Hantar dan baca respons rujukan",
      detail:
        "Ia menghantar permintaan rujukan berstruktur kepada setiap perujuk dan membaca balasan apabila tiba, mengekstrak jawatan disahkan, tarikh dan (jika diberi) sebab meninggalkan kerja — supaya perekrut tidak mengejar dan menyalin panggilan telefon secara manual.",
    },
    {
      title: "Selaraskan sejarah yang dinyatakan",
      detail:
        "Ia membina garis masa daripada CV dan menyemaknya untuk jurang, pekerjaan bertindih dan tarikh yang bercanggah dengan sijil atau balasan rujukan — semakan tempoh dibesar-besarkan dan jurang tidak dijelaskan yang terlepas dalam semakan manual tergesa-gesa.",
    },
    {
      title: "Gabungkan kepada satu ringkasan",
      detail:
        "Ia menggabungkan hasil selari kepada satu ringkasan satu halaman: semakan yang lulus, dan dua atau tiga perkara untuk dilihat — setiap satu dengan sijil, balasan perujuk atau baris CV asalnya.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Pengurus pengambilan menyemak perkara yang ditandakan dan membuat keputusan pengambilan. Tiada apa-apa ditolak secara automatik — ejen memaparkan bukti, manusia memutuskan.",
    },
  ],
  architecture: [
    "Corak: aliran parallelization (pembahagian seksyen) atas Claude Agent SDK — semakan sijil, rujukan dan sejarah dihantar sebagai sub-tugas serentak dan digabungkan semula, setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: setiap cabang selari hanya mendapat hirisan yang diperlukannya (cabang sijil melihat sijil + dakwaan CV yang diujinya, bukan seluruh himpunan) — pilihan kejuruteraan-konteks yang menjaga setiap semakan fokus, lebih murah, dan terpencil daripada suntikan.",
    "Alat: berasingan baca vs tulis — pengekstrak sijil/OCR baca-sahaja, penghurai CV/sejarah dan penyelaras garis masa di satu pihak; satu penghantar permintaan-rujukan yang berpagar dan idempoten di pihak lain (ulangan tidak boleh menghantar e-mel dua kali kepada perujuk).",
    "Pagar keselamatan & keistimewaan-minimum: output JSON berkunci-skema, tiada akses tulis ke sistem ATS/HR teras, ia tidak pernah menolak calon secara automatik, dan sijil keyakinan-rendah atau rujukan tidak sepadan dihalakan ke baris gilir perekrut.",
    "Input tidak dipercayai & PDPA/PDPC: teks sijil dan balasan perujuk dianggap tidak dipercayai (tahan suntikan-prompt — PDF diusik atau e-mel rujukan berniat jahat tidak boleh mengemudi hasil); nombor IC, gaji dan PII calon dikendalikan selaras PDPA/PDPC dengan residensi data dalam skop.",
    "Pemulihan & eskalasi: perujuk tidak membalas, imbasan tidak boleh dibaca atau hasil bercanggah dicuba semula kemudian dieskalasi kepada manusia — setiap cabang berakhir pada seseorang, tidak pernah lulus senyap atau tolak senyap.",
    "Kebolehlihatan & audit: setiap semakan selari, panggilan alat dan hasil gabungan dilog dan boleh dijejak, jadi mana-mana ringkasan pengesahan — dan keputusan akhir perekrut terhadapnya — boleh dibina semula jika calon yang ditolak mencabarnya.",
    "Rangka penilaian: set emas himpunan calon sejarah — termasuk sijil diusik diketahui, tempoh dibesar-besarkan dan jurang tidak dijelaskan — dengan semakan deterministik serta LLM-sebagai-hakim untuk panggilan kabur, diuji regresi pada setiap perubahan sebelum menyentuh calon langsung.",
  ],
  models: [
    {
      task: "Baca & sahkan sijil (termasuk imbasan & foto)",
      model: "Claude Sonnet",
      why: "Visi yang boleh dipercayai dan ekstrak berstruktur pada imbasan sijil dan foto telefon dunia sebenar.",
    },
    {
      task: "Selaraskan sejarah & menaakul merentas semakan",
      model: "Claude Sonnet",
      why: "Kuda kerja — penaakulan kuat untuk menyelaras tarikh, jawatan dan rujukan serta menangkap percanggahan halus.",
    },
    {
      task: "Klasifikasi & ekstrak balasan rujukan",
      model: "Claude Haiku",
      why: "Paling murah dan pantas untuk menarik jawatan/tarikh disahkan daripada e-mel perujuk yang mudah.",
    },
    {
      task: "Adjudikasi kes sempadan / dipertikaikan",
      model: "Claude Opus",
      why: "Digunakan hanya apabila semakan benar-benar samar — sebahagian kecil calon.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap calon",
        value: "~RM 0.50–1.00",
        note: "baca sijil + hurai rujukan + penyelarasan + ringkasan, pada kadar token Claude Sonnet/Haiku",
      },
      {
        label: "Penggunaan AI — pada 100 calon/bulan",
        value: "~RM 45–85 / bulan",
        note: "lebih kurang separuh dengan Batch API untuk larian pengesahan bukan segera pada waktu malam",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "ejen satu saluran yang fokus adalah beberapa minggu kejuruteraan; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding masa perekrut yang dibebaskan dan satu pengambilan salah yang dicegah",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada bilangan sijil dan perujuk serta kerumitan kes. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  howWeBuild:
    "Ia disambung terus ke ATS atau peti masuk pengambilan anda melalui alat yang disambung, jadi imbasan sijil dan balasan perujuk mengalir masuk tanpa eksport manual. Kerana semakan berjalan selari dan setiap satu menulis buktinya sambil berjalan, ringkasan yang dibaca perekrut anda bersumber sepenuhnya — setiap tanda dijejak kembali ke sijil, balasan perujuk atau baris CV asalnya — dan ejen tidak pernah menolak calon secara sendiri.",
  agentLoop: [
    "himpunan calon — sijil, perujuk & sejarah dinyatakan",
    "jalankan semakan secara selari & minta rujukan (alat MCP)",
    "selaraskan hasil selari & semak semula tandanya sendiri",
  ],
  integrations: [
    "Sistem ATS / pengambilan anda",
    "Peti masuk pengambilan atau simpanan dokumen (imbasan sijil)",
    "Saluran e-mel / permintaan rujukan untuk perujuk",
    "Pilihan: semakan pengesahan pendidikan atau kelayakan",
  ],
  modelChoice: {
    approach: "Model tunggal, cabang selari",
    rationale:
      "Semakan adalah bebas tetapi setiap satu tugas yang terhad dan tertakrif jelas berkongsi konteks calon yang sama, jadi satu model yang lengkap menjalankan cabang selari adalah pilihan tepat — boleh dipercayai, lebih murah, dan mudah diaudit — bukannya reka bentuk berbilang-ejen penuh.",
    costNote:
      "Membahagi kepada cabang selari bukan sama dengan sistem berbilang-ejen ≈15× token; kami menyimpan itu untuk penyelidikan luas dan terbuka, bukan set semakan pengesahan yang tetap.",
  },
  evaluation: [
    "Semakan deterministik dahulu — medan sijil hadir, tarikh sepadan, perujuk membalas",
    "Pengesahan sendiri — ejen menyemak semula setiap tanda terhadap sumbernya sebelum memaparkannya",
    "KPI perniagaan — masa perekrut setiap calon, masa-untuk-lulus, ketekalan, dan kes diusik/dibesar-besarkan yang ditangkap",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur penapisan manual semasa pada sampel — masa perekrut setiap calon, berapa lama pengesahan penuh mengambil masa dari mula hingga akhir, sejauh mana konsisten dua perekrut mencapai keputusan sama, dan berapa banyak isu terlepas. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mencapai hasil pengesahan yang betul?",
        metrics: [
          {
            name: "Persetujuan dengan perekrut kanan",
            measures: "Ringkasan, tanda dan semakan yang disyorkan sepadan dengan pakar pada himpunan calon terpencil",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah pendekatannya kukuh dan lengkap?",
        metrics: [
          {
            name: "Semakan diperlukan dilaksanakan",
            measures: "Setiap semakan wajib — setiap sijil, setiap perujuk, garis masa penuh — dijalankan, tiada dilangkau",
            target: "100%",
          },
          {
            name: "Keselarian berkesan",
            measures: "Semakan bebas benar-benar berjalan serentak, mengecilkan masa kalendar, tanpa menjatuhkan mana-mana",
            target: "dipantau",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan ekstrak medan sijil",
            measures: "Adakah ia membaca setiap sijil — institusi, kelayakan, tarikh — dengan betul?",
            target: "≥ 98%",
          },
          {
            name: "Recall percanggahan (kadar tangkap)",
            measures: "Daripada percanggahan sebenar dalam set emas — sijil diusik, tempoh dibesar-besarkan, jurang — berapa yang ditandakan",
            target: "≥ 95%",
          },
          {
            name: "Kadar tanda palsu",
            measures: "Berapa kerap ia menandakan sesuatu yang bukan masalah sebenar",
            target: "≤ 10%",
          },
          {
            name: "Tiada penemuan halusinasi",
            measures: "Setiap tanda dalam ringkasan boleh dijejak ke sijil, balasan perujuk atau baris CV",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menyentuh calon langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas himpunan sejarah — termasuk sijil diusik diketahui dan sejarah dibesar-besarkan. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas himpunan calon sejarah sebenar anda — termasuk yang mempunyai sijil diusik dan tempoh dibesar-besarkan yang anda sudah tangkap — dan membuktikan kebolehpercayaan sebelum ia menyentuh calon langsung. Pelancaran satu saluran yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Adakah pasukan HR anda mengesahkan calon secara manual?",
  ctaDescription:
    "Beritahu kami volum pengambilan anda dan apa yang anda semak hari ini, dan kami akan beritahu dengan jujur sama ada ejen pengesahan HR berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Adakah ejen menolak calon?",
      answer:
        "Tidak. Ia membaca, mengesahkan dan menandakan; pengurus pengambilan manusia membuat keputusan ambil atau tidak. Manusia-dalam-gelung penting — ejen memaparkan bukti, ia tidak pernah menolak calon secara automatik.",
    },
    {
      question: "Bagaimana data calon dikekalkan patuh PDPA/PDPC?",
      answer:
        "Dengan jejak audit penuh supaya mana-mana pengesahan boleh dibina semula, dan dibina mengikut keperluan PDPA/PDPC tentang di mana data calon — nombor IC, sijil, gaji — diproses, disimpan dan diakses. Anggap ini sebagai titik permulaan untuk pasukan pematuhan anda, bukan nasihat undang-undang.",
    },
    {
      question: "Bolehkah ia benar-benar menangkap sijil yang diusik?",
      answer:
        "Ia menangkap ketidaktekalan dalaman dan percanggahan terhadap CV dan rujukan, dan menandakan sijil yang kelihatan diubah. Untuk pengesahan berwibawa kami boleh menyambung semakan pengesahan pendidikan atau kelayakan — tetapi penilaian akhir sentiasa dengan perekrut anda.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep untuk satu saluran pengambilan yang fokus biasanya beberapa minggu; pelancaran penuh mengambil masa lebih lama, kebanyakannya kerana integrasi dan membiasakan perekrut, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "人力资源",
  title: "人力资源核实智能体",
  cardSummary: "在发出录用之前，核实候选人的证书、推荐人与所述经历是否对得上。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，将候选人的证书、推荐人与所述工作经历同时通过并行核查，带证据地标记每一处不一致，并为你的 HR 团队提供可直接录用的核实摘要——而录用决定仍由人来做。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "在区域内，一次错误招聘要付出数月薪资和重新招聘的成本——这能在发出录用前抓出被篡改的证书或注水的任职时长，并在几分钟内放行合格候选人，而非几天。",
    complexityLabel: "中等",
    complexityWhy:
      "对证书扫描件和推荐人邮件的视觉/OCR、多项并行核查的汇总，以及对身份证号和候选人数据符合 PDPA/PDPC 的处理——但没有资金流动，关键路径上也没有监管方。",
    timeToLive: "约数周（单一招聘管道）",
    bestFit: "在发出录用前审查候选人的 HR 团队与招聘机构",
  },
  outcomes: [
    "在发出录用前抓出被篡改的证书或注水的任职时长——而非在一次昂贵的错误招聘之后",
    "合格候选人几分钟内放行，好的人选不会流失给更快的竞争对手",
    "对每位候选人做同样的核查——不因偏爱某份简历而走捷径，如遭质疑有审计轨迹",
    "HR 招聘人员回到面试与成交，而不是打电话追推荐人",
  ],
  impact: {
    manualLabel: "当前人工审查",
    manualValue: "约 2–4 小时（分散在数天）",
    agentLabel: "使用智能体",
    agentValue: "约 10–15 分钟复核",
    saved:
      "每位候选人约节省 2–3 小时招聘人员时间，并把数天的日历时间压缩到当天。以每月 80 次招聘计，约合 160–240 小时——大约相当于释放一名全职招聘人员去成交。而一次被阻止的错误招聘（往往是 3–6 个月薪资）就足以数倍地覆盖整个构建成本。",
    note: "为估算；实际时间取决于证书与推荐人数量，以及推荐人回复的快慢。",
  },
  businessProblem:
    "区域内的雇主或招聘机构将每位候选人作为一整套材料审查：一份简历、扫描的 SPM/A-levels、文凭或学位证书、几位推荐人联系方式，以及所述的工作经历。招聘人员一天的大部分时间花在机械且大量等待的环节上——阅读每份证书、通过电话和邮件追推荐人、用肉眼核对所述日期与职位是否真的与简历对得上——而不是判断人岗匹配。这个过程缓慢且会停滞：推荐核实无人回复地拖上数天，好候选人却接受了别处的录用。而由于是人工，尽职程度悄悄地因人而异——赶时间的职位得到的核查比清闲那周更轻，被篡改的证书或注水的任职时长就此漏过。",
  manualWorkflow: {
    steps: [
      "打开候选人材料——简历、证书扫描件、推荐人名单",
      "用肉眼阅读每份证书，记下院校、学历与日期",
      "给每位推荐人发邮件、打电话，然后等上数天回复",
      "把所述职位、日期与薪资与简历及推荐核实交叉核对",
      "追查缺口——缺失的证书、重叠的任职、联系不上的推荐人",
      "之后才形成看法并推进到录用",
    ],
    breaks:
      "瓶颈在于核实——阅读文件和等待推荐核实——是串行完成的，一次一项。这个缓慢重复的环节会停滞数天，且在招聘人员之间悄悄地在细致程度上参差不齐。",
  },
  agenticWorkflow: {
    narrative:
      "我们将审查重新设计为 parallelization（并行/分段）工作流：智能体不再一次核查一项，而是把候选人材料拆分成互不依赖的核查——证书、推荐核实与所述经历——让它们同时进行，再把结果汇总成一份有证据支撑的摘要。独立核查并行进行，正是把数天压缩为数分钟的关键。",
    steps: [
      "把材料拆分成独立核查",
      "核实证书（读取 + 验证）",
      "发送并读取推荐人回复",
      "核对所述经历的缺口与重叠",
      "汇总成一份有证据的摘要",
    ],
    inLabel: "一份候选人材料进入",
    outLabel: "一份可直接录用的核实摘要",
    loopAround: {
      index: 0,
      label: "证书、推荐核实与经历核查并行进行，而非一个接一个——即 parallelization（并行/分段）模式。",
    },
    humanStep: "招聘经理做出录用决定",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "在候选人材料中",
    inMessage:
      "简历：“高级分析师，Maju Corp，2020 年 1 月 – 至今”。推荐人（Maju Corp HR）回复：“任职分析师，2021 年 3 月至 2023 年 8 月”。学位证书：理学士，Universiti XYZ，2019 年授予。",
    outLabel: "智能体标记出什么",
    outLines: [
      "学位证书已读取并验证——院校、学历与年份齐全",
      "推荐人回复已收到，并与所述雇主匹配",
      "所述职位“高级分析师”与推荐人的“分析师”交叉核对",
      "所述日期与推荐人确认的日期交叉核对",
    ],
    outResult:
      "标记 2 处差异：职位（所述高级分析师 vs 确认为分析师）· 任职时长（所述 2020 年 1 月–至今 vs 确认为 2021 年 3 月–2023 年 8 月）",
    note: "每个标记都引用确切的文件或推荐人回复 · 由你的招聘人员做录用决定 · 每位候选人约 RM 0.50–1.00。",
  },
  workflowDetail: [
    {
      title: "把材料拆分成独立核查",
      detail:
        "智能体拿到候选人材料——简历、证书扫描件、推荐人联系方式、所述经历——将其拆分成互不依赖的核实轨道，因此它们可以同时进行，而不是排在队列里。",
    },
    {
      title: "核实证书",
      detail:
        "它读取每份证书——包括手机照片和扫描件——用视觉能力提取院校、学历与日期，检查其内部一致性以及与简历所称内容的一致性，并标记任何看起来被改动或对不上的地方。",
    },
    {
      title: "发送并读取推荐人回复",
      detail:
        "它向每位推荐人发送结构化的推荐核实请求，并在回复到达时读取，提取确认的职位、日期以及（若提供）离职原因——这样招聘人员无需人工追问并誊写电话内容。",
    },
    {
      title: "核对所述经历",
      detail:
        "它根据简历构建一条时间线，检查其中的缺口、重叠任职，以及与证书或推荐核实回复相矛盾的日期——这正是仓促的人工审查会漏掉的注水任职与不明缺口核查。",
    },
    {
      title: "汇总成一份摘要",
      detail:
        "它把并行结果汇总成一页式摘要：哪些核查通过，以及需要关注的两三项——每一项都附带其来源的证书、推荐人回复或简历行。",
    },
    {
      title: "交给人处理",
      detail:
        "招聘经理审阅被标记的项目并做出录用决定。没有任何内容会被自动拒绝——智能体呈现证据，由人来决定。",
    },
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的 parallelization（并行/分段）工作流——证书、推荐核实与经历核查作为并发子任务分派并汇总回来，每个工具通过进程内 MCP 服务器提供。",
    "上下文：每个并行分支只拿到它所需的切片（证书分支只看到证书 + 它所验证的那条简历声明，而非整份材料）——这一上下文工程选择让每项核查更聚焦、更省成本、也与注入相互隔离。",
    "工具：读写分离——一侧是只读的证书/OCR 提取器、简历/经历解析器与时间线核对器；另一侧是单一的、带门控且幂等的推荐核实请求发送器（重试不会给推荐人重复发邮件）。",
    "防护机制与最小权限：严格的 JSON 模式输出、对 ATS/HR 记录系统无写入权限、它绝不自动拒绝候选人，低置信度的证书或未匹配的推荐核实会转入招聘人员队列。",
    "不可信输入与 PDPA/PDPC：证书文本与推荐人回复被视为不可信（可抵御提示注入——被篡改的 PDF 或恶意的推荐邮件无法左右结果）；身份证号、薪资与候选人 PII 均按 PDPA/PDPC 处理，数据驻留在考量范围内。",
    "恢复与上报：不回复的推荐人、无法识别的扫描件或相互冲突的结果会先重试再上报给人——每个分支都终结于某个人，绝不静默放行或静默拒绝。",
    "可观测性与审计：每项并行核查、工具调用与汇总结果都会留痕且可追溯，因此任何核实摘要——以及招聘人员据此做出的最终判断——在被拒候选人提出质疑时都能被还原。",
    "评估框架：一组历史候选人材料的黄金集——含已知的被篡改证书、注水任职与不明缺口——配合确定性检查，并对模糊判断采用 LLM 作为评审，每次改动在接触实时候选人前都经过回归测试。",
  ],
  models: [
    {
      task: "读取并核实证书（含扫描件与照片）",
      model: "Claude Sonnet",
      why: "对真实世界的证书扫描件与手机照片具备可靠的视觉与结构化提取能力。",
    },
    {
      task: "核对经历并跨核查进行推理",
      model: "Claude Sonnet",
      why: "主力模型——强推理能力，用以对齐日期、职位与推荐核实并抓出细微的不一致。",
    },
    {
      task: "分类并提取推荐人回复",
      model: "Claude Haiku",
      why: "从直接的推荐人邮件中提取确认的职位/日期，最便宜、最快。",
    },
    {
      task: "裁决临界 / 有争议的案例",
      model: "Claude Opus",
      why: "仅在某项核查确实含糊时调用——占候选人的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每位候选人",
        value: "约 RM 0.50–1.00",
        note: "证书读取 + 推荐核实解析 + 核对 + 摘要，按 Claude Sonnet/Haiku 的 token 费率",
      },
      {
        label: "AI 用量——每月 100 位候选人",
        value: "约 RM 45–85 / 月",
        note: "使用 Batch API 处理非紧急的夜间核实任务约可减半",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "聚焦单一管道的智能体约需数周工程；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比其释放的招聘人员时间与阻止的一次错误招聘，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于证书与推荐人数量以及案件复杂度。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  howWeBuild:
    "它通过连接的工具直接接入你的 ATS 或招聘收件箱，因此证书扫描件与推荐人回复无需手动导出即可流入。因为核查并行进行、每一项边做边写下其证据，你的招聘人员所读到的摘要都有完整来源——每个标记都能追溯到其来源的确切证书、推荐人回复或简历行——而智能体绝不会自行拒绝候选人。",
  agentLoop: [
    "候选人材料——证书、推荐人与所述经历",
    "并行运行各项核查并请求推荐核实（MCP 工具）",
    "汇总并行结果并自查其标记",
  ],
  integrations: [
    "你的 ATS / 招聘系统",
    "招聘收件箱或文档库（证书扫描件）",
    "面向推荐人的邮件 / 推荐核实请求渠道",
    "可选：教育或资历核验查询",
  ],
  modelChoice: {
    approach: "单一模型，并行分支",
    rationale:
      "这些核查彼此独立，但每一项都是界限清晰、共享同一候选人上下文的有界任务，因此一个完善配置的单一模型运行并行分支是正确选择——可靠、更省成本、也易审计——而非一套完整的多智能体设计。",
    costNote:
      "拆分成并行分支并不等同于约 15 倍 token 的多智能体系统；我们把那留给真正开放、广度优先的研究，而非一组固定的核实检查。",
  },
  evaluation: [
    "先做确定性检查——证书字段齐全、日期对得上、推荐人已回复",
    "自我验证——智能体在呈现前将每个标记与其来源再次核对",
    "业务 KPI——每位候选人的招聘人员时间、放行耗时、一致性，以及拦截到的篡改/注水案例",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量当前的人工审查——每位候选人的招聘人员耗时、完整核实端到端所需时间、两名招聘人员得出相同结论的一致程度、以及有多少问题漏掉。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否达成了正确的核实结果？",
        metrics: [
          {
            name: "与资深招聘人员的一致度",
            measures: "在留出的候选人材料上，其摘要、标记与建议核查是否与专家一致",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "方法是否合理且完整？",
        metrics: [
          {
            name: "必需核查全部执行",
            measures: "每一项规定核查——每份证书、每位推荐人、完整时间线——都执行，不遗漏",
            target: "100%",
          },
          {
            name: "有效的并行",
            measures: "独立核查确实并发进行、压缩日历时间，且不遗漏任何一项",
            target: "持续监控",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "证书字段提取准确率",
            measures: "是否正确读取每份证书——院校、学历、日期？",
            target: "≥ 98%",
          },
          {
            name: "差异召回率（拦截率）",
            measures: "黄金集中真实的不一致——被篡改证书、注水任职、缺口——有多少被标记出来",
            target: "≥ 95%",
          },
          {
            name: "误报率",
            measures: "多久会标记出并非真正问题的内容",
            target: "≤ 10%",
          },
          {
            name: "无凭空捏造的发现",
            measures: "摘要中的每个标记都能追溯到某份证书、推荐人回复或简历行",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于历史材料黄金集（含已知的被篡改证书与注水经历）上超过人工基线并达到这些 GPA 目标之前，它不会接触任何实时候选人。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史候选人材料上做概念验证——包括你已经抓到的那些带篡改证书和注水任职的案例——在接触实时候选人前先证明可靠性。聚焦单一管道的落地只需数周。",
  ctaTitle: "你的 HR 团队还在人工核实候选人吗？",
  ctaDescription:
    "告诉我们你的招聘量以及你今天核查的内容，我们会诚实告诉你人力资源核实智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "智能体会拒绝候选人吗？",
      answer:
        "不会。它负责读取、核实和标记；由人类招聘经理做出录用或不录用的决定。人在环路中至关重要——智能体呈现证据，它绝不自动拒绝候选人。",
    },
    {
      question: "候选人数据如何符合 PDPA/PDPC 要求？",
      answer:
        "通过完整的审计轨迹让任何核实都可被还原，并围绕 PDPA/PDPC 对候选人数据——身份证号、证书、薪资——处理、存储与访问位置的要求来构建。请将此视为你合规团队的起点，而非法律意见。",
    },
    {
      question: "它真的能抓出被篡改的证书吗？",
      answer:
        "它能抓出内部不一致以及与简历和推荐核实之间的矛盾，并标记看起来被改动的证书。若需权威确认，我们可以接入教育或资历核验查询——但最终判断始终由你的招聘人员做出。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一聚焦招聘管道的概念验证通常只需数周；全面落地需要更长时间，主要是因为集成和让招聘人员适应，而不是 AI 本身。",
    },
  ],
};

export const hrVerification: AgentUseCase = {
  structure: {
    slug: "hr-verification",
    status: "available",
    pattern: "parallelization",
    route: "solutions/agent-use-cases/hr-verification",
    heroImage: "/images/agent-use-cases/hr-verification-hero.jpg",
    cardImage: "/images/agent-use-cases/hr-verification-card.jpg",
    relatedUseCases: ["kyc-onboarding", "credit-evaluation"],
    trackLocation: "agent_use_case_hr_verification",
    valueScore: 4,
    complexityScore: 3,
  },
  content: { en, ms, zh },
};
